import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { uploadCV, checkCVExists } from "@/utils/candidate/cv-storage";
import { CVViewer } from "./CVViewer";
import { CVVersionHistory } from "./CVVersionHistory";
import { CVUploadZone } from "./CVUploadZone";
import { toast } from "@/hooks/use-toast";
import { CVFullScreenModal } from "./CVFullScreenModal";
import { triggerReanalysis, testOpenAIConnection } from "@/utils/candidate/ai";
import { useSettings } from "@/providers/SettingsProvider";
import { supabase } from "@/integrations/supabase/client";
import { CVActionButtons } from "./cv/CVActionButtons";
import { CVAnalysisProgress } from "./cv/CVAnalysisProgress";

interface CandidateCVProps {
  cv: string | null;
  cvExists: boolean;
  candidateName: string;
  candidateId: string;
  onCVUpdate?: (newCVUrl: string) => void;
}

export const CandidateCV = ({ cv, cvExists, candidateId, candidateName, onCVUpdate }: CandidateCVProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showUploadZone, setShowUploadZone] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showCVModal, setShowCVModal] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [testingAPI, setTestingAPI] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState("");
  const { devMode } = useSettings();
  const [localCvExists, setLocalCvExists] = useState(cvExists);
  const [localCv, setLocalCv] = useState(cv);

  useEffect(() => {
    setLocalCvExists(cvExists);
    setLocalCv(cv);
  }, [cvExists, cv]);

  const refreshCVStatus = async () => {
    try {
      const { data: resumeList } = await supabase.functions.invoke("get-candidate-resume", {
        body: { candidateId }
      });

      if (resumeList && resumeList.resume && resumeList.resume.currentResume) {
        setLocalCvExists(true);
        setLocalCv(resumeList.resume.currentResume.publicUrl);
      } else {
        setLocalCvExists(false);
      }
    } catch (error) {
      console.error("Error refreshing CV status:", error);
      setLocalCvExists(false);
    }
  };

  const handleFileChange = async (file: File) => {

    setIsLoading(true);

    try {
      
      const newCvUrl = await uploadCV(candidateId, file, cv);

      if (newCvUrl) {
        setLocalCv(newCvUrl);
        setLocalCvExists(true);

        if (onCVUpdate) {
          onCVUpdate(newCvUrl);
        }

        toast({
          title: "CV uploaded successfully",
          description: "Your CV has been saved",
        });

        setIsAnalyzing(true);
        setAnalyzeProgress(30);
        setProgressStatus("Starting analysis...");

        const progressInterval = setInterval(() => {
          setAnalyzeProgress(prev => Math.min(prev + 10, 90));
          setProgressStatus("Processing document...");
        }, 1000);

        await triggerReanalysis(candidateId, newCvUrl);

        clearInterval(progressInterval);
        setAnalyzeProgress(100);
        setProgressStatus("Analysis complete!");

        setTimeout(() => {
          setIsAnalyzing(false);
          refreshCVStatus();
        }, 1000);

        await logCandidateHistory(candidateId, "CV_UPLOADED", "Candidate CV was uploaded");
      }
    } catch (error) {
      console.error("Error uploading CV:", error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your CV",
        variant: "destructive",
      });
      setIsAnalyzing(false);
    } finally {
      setIsLoading(false);
      setShowUploadZone(false);
    }
  };

  const handleViewCV = () => {
    if (localCvExists && localCv) {
      setShowCVModal(true);
    } else {
      toast({
        title: "No CV available",
        description: "Please upload a CV first",
        variant: "destructive",
      });
    }
  };

  const handleTestAPI = async () => {
    setTestingAPI(true);
    try {
      const result = await testOpenAIConnection();
      if (result.success) {
        toast({
          title: "API Test Successful",
          description: result.message,
        });
      } else {
        toast({
          title: "API Test Failed",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error testing API:", error);
      toast({
        title: "API Test Error",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setTestingAPI(false);
    }
  };

  const toggleUploadZone = () => {
    setShowUploadZone(!showUploadZone);
  };

  const toggleVersionHistory = () => {
    setShowVersions(!showVersions);
  };

  const logCandidateHistory = async (
    candidateId: string,
    action: string,
    description: string
  ) => {
    try {
      const { error } = await supabase
        .from('candidate_history')
        .insert({
          candidate_id: candidateId,
          action: action,
          description: description,
          performed_by: "Current User",
        });

      if (error) {
        console.error("Error logging to history:", error);
      }
    } catch (error) {
      console.error("Error in logCandidateHistory:", error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-3">
        <h3 className="text-lg font-semibold mb-2">CV</h3>
      </div>

      <CVVersionHistory
        candidateId={candidateId}
        isVisible={showVersions}
        onVersionSelect={() => setShowVersions(false)}
      />

      <div className="border border-gray-200 rounded-lg flex-1 flex items-center justify-center bg-gray-50 overflow-hidden relative mb-4">
        {showUploadZone ? (
          <CVUploadZone
            onFileSelect={handleFileChange}
            isLoading={isLoading || isAnalyzing}
            progress={analyzeProgress}
            progressStatus={progressStatus}
          />
        ) : (
          <CVViewer
            cv={localCv}
            cvExists={localCvExists}
            onView={handleViewCV}
            onUpload={toggleUploadZone}
          />
        )}
      </div>

      <div className="flex flex-col gap-2 w-full">
        <CVActionButtons
          cvExists={localCvExists}
          showUploadZone={showUploadZone}
          toggleUploadZone={toggleUploadZone}
          isLoading={isLoading}
          isAnalyzing={isAnalyzing}
          onTestAPI={handleTestAPI}
          devMode={devMode}
          testingAPI={testingAPI}
          onViewHistory={toggleVersionHistory}
          candidateName={candidateName}
          candidateId={candidateId}
        />
      </div>

      {showCVModal && localCvExists && localCv && (
        <CVFullScreenModal
          cvUrl={localCv}
          onClose={() => setShowCVModal(false)}
        />
      )}
    </div>
  );
};
